import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useAddUnreleasedCatalogItemMutation, usePatchCatalogItemMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { addSongErrorOf, AddSongErrorKind } from '../addSongError';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
import { SongLinkPath, SongLinkPathMode } from './SongLinkPath';
import { FirstSongKind } from '../useOnboardingFirstSong';
import { BioLinkTick } from '../useBioLinkTick';
import { BioLinkTickRow } from '../BioLinkTickRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongStepProps {
  accountId: number;
  /** The saved row, when the user came back to step 1. */
  item?: CatalogItem;
  artistName: string;
  eyebrow: string;
  title: string;
  onTitleChange: (title: string) => void;
  track: TrackSave;
  /** The song is saved: the page opens the next step. `bioLink`: what the Bio Link auto add did. */
  onSaved: (item: CatalogItem, kind: FirstSongKind | null, bioLink?: BioLinkAutoAddResult) => void;
  /** "Auto add to my Bio Link" (design smartlink-editor e1). */
  bioTick: BioLinkTick;
  /** Step 1 of a saved row: Next goes on with no new row. */
  onNext: () => void;
  /** The first input of the step (the onboarding analytics). */
  onStarted?: (kind: FirstSongKind) => void;
}

const TITLE_MAX = 500;

/**
 * Step 1 (design catalog-add-song a1, a2, h1-h3): the title and the artist. Next saves the song
 * (`POST .../catalog/unreleased`), so closing the page later loses nothing. "Find it" and "Paste a
 * link" open the path of a released song (design c1-c3): a search pick is claimed, a link is added.
 */
const SongStepComponent: React.FC<SongStepProps> = ({
  accountId,
  item,
  artistName,
  eyebrow,
  title,
  onTitleChange,
  track,
  onSaved,
  onNext,
  onStarted,
  bioTick,
}) => {
  const intl = useIntl();
  const [artist, setArtist] = useState(item?.artistName ?? artistName);
  const [isTouched, setTouched] = useState(false);
  const [linkPath, setLinkPath] = useState<SongLinkPathMode>();
  const [error, setError] = useState<AddSongErrorKind>();
  const [addUnreleased, adding] = useAddUnreleasedCatalogItemMutation();
  const [patchItem, patching] = usePatchCatalogItemMutation();
  const isSaving = adding.isLoading || patching.isLoading;
  const isClaimed = item?.kind === CatalogItemKind.claimed;
  const trimmed = title.trim();
  const isTitleMissing = trimmed === '';
  const isTooLong = trimmed.length > TITLE_MAX || artist.trim().length > TITLE_MAX;

  const save = useCallback(() => {
    setTouched(true);
    if (isTitleMissing || isTooLong || isSaving) {
      return;
    }
    setError(undefined);
    if (item !== undefined) {
      const isChanged = item.title !== trimmed || item.artistName !== artist.trim();
      if (isClaimed || !isChanged) {
        onNext();
        return;
      }
      track(patchItem({ accountId, itemUuid: item.uuid, title: trimmed, artistName: artist.trim() }).unwrap())
        .then(() => onNext())
        .catch((failure: unknown) => setError(addSongErrorOf(failure)));
      return;
    }
    const { addToBioLink } = bioTick;
    track(addUnreleased({ accountId, title: trimmed, artistName: artist.trim() || artistName, addToBioLink }).unwrap())
      .then((result) => onSaved(result.item, 'unreleased', result.bioLink))
      .catch((failure: unknown) => setError(addSongErrorOf(failure)));
  }, [
    accountId,
    addUnreleased,
    artist,
    artistName,
    bioTick,
    isClaimed,
    isSaving,
    isTitleMissing,
    isTooLong,
    item,
    onNext,
    onSaved,
    patchItem,
    track,
    trimmed,
  ]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        save();
      }
    },
    [save]
  );

  if (linkPath !== undefined && item === undefined) {
    return (
      <SongLinkPath
        accountId={accountId}
        artistName={artistName}
        eyebrow={eyebrow}
        track={track}
        onSaved={onSaved}
        onTypeInstead={() => setLinkPath(undefined)}
        onStarted={onStarted}
        bioTick={bioTick}
        mode={linkPath}
        initialQuery={linkPath === 'find' ? trimmed : undefined}
      />
    );
  }

  const showMissing = isTouched && isTitleMissing;

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(addSongFlowMessages.songQuestion)}
        sub={intl.formatMessage(addSongFlowMessages.songSub)}
      />
      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-song-title">
          {intl.formatMessage(addSongFlowMessages.songTitle)}
        </label>
        <input
          id="add-song-title"
          className={clsx(styles.input, styles.inputBig, showMissing && styles.inputBad)}
          value={title}
          placeholder={intl.formatMessage(addSongFlowMessages.songTitle)}
          maxLength={TITLE_MAX}
          disabled={isSaving || isClaimed}
          aria-invalid={showMissing}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the one field of the step
          autoFocus
          onChange={(event) => {
            onStarted?.('unreleased');
            onTitleChange(event.target.value);
          }}
          // No check when the field loses the focus: a tap on "Find it" moved the button down
          // under the finger with an error line (QA 2026-10-28, mob-4). Next checks the title.
          onKeyDown={onKeyDown}
        />
        {showMissing && (
          <span className={styles.error} role="alert">
            {intl.formatMessage(addSongFlowMessages.songTitleMissing)}
          </span>
        )}
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-song-artist">
          {intl.formatMessage(addSongFlowMessages.songArtist)}
        </label>
        <input
          id="add-song-artist"
          className={styles.input}
          value={artist}
          maxLength={TITLE_MAX}
          disabled={isSaving || isClaimed}
          onChange={(event) => setArtist(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <span className={styles.hint}>{intl.formatMessage(addSongFlowMessages.songArtistHint)}</span>
      </div>
      {item === undefined && <BioLinkTickRow tick={bioTick} disabled={isSaving} />}
      {item === undefined && (
        <div className={styles.shortcut}>
          <span className={styles.kbox}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
          <div className={styles.grow}>
            <b>{intl.formatMessage(addSongFlowMessages.songFindTitle)}</b>
            <small>{intl.formatMessage(addSongFlowMessages.songFindBody)}</small>
          </div>
          <div className={clsx('d-flex flex-wrap gap-2 justify-content-end', styles.shortcutButtons)}>
            <SMLButton
              size="sm"
              color="primary"
              omitCap
              onClick={() => setLinkPath('find')}
              testID="add-song-find-song"
            >
              {intl.formatMessage(addSongFlowMessages.songFindButton)}
            </SMLButton>
            <SMLButton size="sm" outline omitCap onClick={() => setLinkPath('paste')} testID="add-song-paste-link">
              {intl.formatMessage(addSongFlowMessages.songShortcutButton)}
            </SMLButton>
          </div>
        </div>
      )}
      {error !== undefined && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(
            error === 'readOnly' ? addSongFlowMessages.readOnlyArtist : addSongFlowMessages.saveFailed
          )}
        </div>
      )}
      <StepActions
        note={item === undefined ? intl.formatMessage(addSongFlowMessages.songSaveNote) : undefined}
        main={{
          label: intl.formatMessage(error === undefined ? addSongFlowMessages.next : addSongFlowMessages.tryAgain),
          // On with no title: a press says why under the field (QA imp-3).
          onClick: save,
          isLoading: isSaving,
        }}
      />
    </>
  );
};

export const SongStep = TypedMemo(SongStepComponent);
